import { memo, useCallback, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";

import type { MessageTemplate } from "../types";
import {
  isFluxmedDefaultMessageTemplate,
  isMessageTemplateApproved,
  type FluxmedDefaultTemplateCategory,
  type MessageTemplateListScope,
  resolveDefaultTemplatesHasActiveMetaFlag,
} from "../utils/messageTemplateUiUtils";
import ClinicalCrmContextInfoBanner from "./ClinicalCrmContextInfoBanner";
import MessageDefaultTemplatesCategoryFilter from "./MessageDefaultTemplatesCategoryFilter";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { getFluxmedDefaultTemplateUsageShortMessage } from "../utils/fluxmedDefaultTemplateUsage";
import { useMessageTemplatesSectionState } from "../hooks/useMessageTemplatesSectionState";
import MessageTemplateFormDialog from "./MessageTemplateFormDialog";
import MessageTemplateDeleteConfirmDialog from "./MessageTemplateDeleteConfirmDialog";
import MessageTemplatesCardsView from "./MessageTemplatesCardsView";
import MessageTemplatesTableView from "./MessageTemplatesTableView";
import MessageTemplatesToolbar from "./MessageTemplatesToolbar";
import {
  SendWhatsAppMessageDialog,
  useMessagingTemplatesForSend,
} from "src-next/modules/crm-clinical/messaging";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import type { MessageTemplateQuestionnaireOption } from "./MessageTemplateFormDialog";
import { oids } from "@/configs/Settings";
import { InfoOutlined } from "@mui/icons-material";

export interface MessageTemplatesSectionProps {
  organizationId?: string;
  practitionerId?: string;
  templateScope?: MessageTemplateListScope;
  templates: MessageTemplate[];
  onCreateTemplate: (params: {
    input: Pick<
      MessageTemplate,
      | "name"
      | "preview"
      | "header"
      | "buttonType"
      | "buttonText"
      | "buttonValue"
    >;
  }) => Promise<void>;
  onUpdateTemplate: (params: { input: MessageTemplate }) => Promise<void>;
  onDeleteTemplate: (params: { templateId: string }) => Promise<void>;
  onRefreshTemplates?: () => void | Promise<void>;
  isTemplatesLoading?: boolean;
  onMessageSent?: () => void;
  franchiseRefreshSignal?: number;
  hasActiveMetaEndpoint?: boolean;
  isEndpointStatusLoading?: boolean;
  whatsappChannelOption?: "fluxmedDefault" | "metaEmbeddedSignup" | null;
  /**
   * Questionários do formulário só depois da seção aberta.
   * A lista em si vem do cache compartilhado.
   */
  questionnairesEnabled?: boolean;
  /** Categoria pré-selecionada ao navegar a partir do painel de automações. */
  requestedDefaultCategory?: FluxmedDefaultTemplateCategory | null;
  onRequestedDefaultCategoryConsumed?: () => void;
}

const MessageTemplatesSection = memo(
  ({
    templates,
    organizationId,
    practitionerId,
    templateScope = "organization",
    onCreateTemplate,
    onUpdateTemplate,
    onDeleteTemplate,
    onRefreshTemplates,
    isTemplatesLoading = false,
    onMessageSent,
    franchiseRefreshSignal = 0,
    hasActiveMetaEndpoint = false,
    isEndpointStatusLoading = false,
    whatsappChannelOption = null,
    questionnairesEnabled = true,
    requestedDefaultCategory = null,
    onRequestedDefaultCategoryConsumed,
  }: MessageTemplatesSectionProps) => {
    const resolvedHasActiveMetaEndpoint =
      resolveDefaultTemplatesHasActiveMetaFlag({
        whatsappChannelOption,
        hasActiveMetaEndpoint,
        isConnectionStatusLoading: isEndpointStatusLoading,
      });

    const {
      availableStatuses,
      createFormFlow,
      currentPage,
      editFormFlow,
      filteredTemplates,
      handleChangeSearchQuery,
      handleChangeSelectedStatuses,
      handleChangeDefaultCategory,
      handleEditTemplate,
      selectedDefaultCategory,
      handleTableRowsPerPageChange,
      isCreateDialogOpen,
      paginatedTemplates,
      rowsPerPage,
      searchQuery,
      selectedStatuses,
      setCurrentPage,
      setIsCreateDialogOpen,
      setTemplateBeingEdited,
      setTemplateIdPendingDelete,
      setTemplatePendingSend,
      setViewMode,
      templateBeingEdited,
      templateIdPendingDelete,
      templatePendingSend,
      totalPages,
      viewMode,
    } = useMessageTemplatesSectionState({
      organizationId,
      templateScope,
      hasActiveMetaEndpoint: resolvedHasActiveMetaEndpoint,
      templates,
      onCreateTemplate,
      onUpdateTemplate,
    });

    const isDefaultTemplatesScope = templateScope === "default";
    const allowSend = !isDefaultTemplatesScope;
    const allowDelete = !isDefaultTemplatesScope;
    const showCreateButton = !isDefaultTemplatesScope;
    const canEditDefaultTemplates =
      !isEndpointStatusLoading && hasActiveMetaEndpoint;
    const allowEdit = isDefaultTemplatesScope ? canEditDefaultTemplates : true;

    const handleEditTemplateGuarded = useCallback(
      (template: MessageTemplate) => {
        if (isDefaultTemplatesScope && !canEditDefaultTemplates) return;
        handleEditTemplate(template);
      },
      [canEditDefaultTemplates, handleEditTemplate, isDefaultTemplatesScope],
    );

    useEffect(() => {
      if (!isDefaultTemplatesScope || !requestedDefaultCategory) return;

      handleChangeDefaultCategory(requestedDefaultCategory);
      onRequestedDefaultCategoryConsumed?.();
    }, [
      handleChangeDefaultCategory,
      isDefaultTemplatesScope,
      onRequestedDefaultCategoryConsumed,
      requestedDefaultCategory,
    ]);

    const isDefaultTemplateRow = useCallback(
      (template: MessageTemplate) =>
        isFluxmedDefaultMessageTemplate(template.name, organizationId, {
          hasActiveMetaEndpoint: resolvedHasActiveMetaEndpoint,
        }),
      [organizationId, resolvedHasActiveMetaEndpoint],
    );

    const {
      questionnaires,
      isLoading: isQuestionnairesLoading,
      errorMessage: questionnairesErrorMessage,
    } = useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
      enabled: questionnairesEnabled,
    });

    /**
     * Templates para envio devem vir do pipeline centralizado baseado no Endpoint rank1,
     * para manter consistência entre telas e evitar WABA errado / double-fetch.
     *
     * Nota: mantemos `templates` (props) para CRUD/listagem nesta seção, pois o fluxo
     * de gerenciamento já tem gating próprio no nível da página/board.
     */
    const { templates: messagingTemplatesForSend } =
      useMessagingTemplatesForSend({
        organizationId,
        sourceTemplates: templates,
      });

    const editingDefaultTemplateUsageMessage = useMemo(() => {
      if (!templateBeingEdited) return null;
      if (
        !isFluxmedDefaultMessageTemplate(
          templateBeingEdited.name,
          organizationId,
          {
            hasActiveMetaEndpoint: resolvedHasActiveMetaEndpoint,
          },
        )
      )
        return null;
      return getFluxmedDefaultTemplateUsageShortMessage(
        templateBeingEdited.name,
        organizationId,
        resolvedHasActiveMetaEndpoint,
      );
    }, [organizationId, resolvedHasActiveMetaEndpoint, templateBeingEdited]);

    const questionnaireOptions = useMemo<MessageTemplateQuestionnaireOption[]>(
      () =>
        questionnaires
          .filter((item) => Boolean(item.id))
          .map((item) => ({
            id: item.id as string,
            title: item.title,
          })),
      [questionnaires],
    );

    return (
      <Stack spacing={0}>
        {isDefaultTemplatesScope ? (
          <Stack spacing={1.5}>
            <ClinicalCrmContextInfoBanner
              variant="info"
              title="Modelos padrão Fluxmed"
              icon={<InfoOutlined />}
              description={
                canEditDefaultTemplates
                  ? "Os modelos WhatsApp padrão são utilizados para envio de mensagens de agendamento e cancelamento de consultas, e cobranças de pagamentos. Esses modelos podem ser alterados, mas não podem ser excluídos."
                  : "Os modelos WhatsApp padrão são utilizados para envio de mensagens de agendamento e cancelamento de consultas, e cobranças de pagamentos. Para personalizá-los, conecte sua conta Meta."
              }
            />
            {canEditDefaultTemplates ? (
              <ClinicalCrmContextInfoBanner
                variant="warning"
                title="Cuidado ao alterar modelos padrão"
                icon={<WarningAmberOutlinedIcon />}
                description="Ao alterar um modelo de mensagem padrão ele ficará pendente de aprovação na Plataforma Meta. Seus pacientes não receberão as mensagens até que o modelo seja aprovado."
              />
            ) : null}
          </Stack>
        ) : null}

        <MessageTemplatesToolbar
          searchQuery={searchQuery}
          onChangeSearchQuery={handleChangeSearchQuery}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          availableStatuses={availableStatuses}
          selectedStatuses={selectedStatuses}
          onChangeSelectedStatuses={handleChangeSelectedStatuses}
          onCreateTemplate={
            showCreateButton ? () => setIsCreateDialogOpen(true) : undefined
          }
          showCreateButton={showCreateButton}
          onRefreshTemplates={onRefreshTemplates}
          isRefreshing={isTemplatesLoading}
        />

        {isDefaultTemplatesScope ? (
          <MessageDefaultTemplatesCategoryFilter
            selectedCategory={selectedDefaultCategory}
            onChangeCategory={handleChangeDefaultCategory}
          />
        ) : null}

        {viewMode === "table" ? (
          <MessageTemplatesTableView
            templates={filteredTemplates}
            onSendTemplate={(template) => {
              if (!allowSend || !isMessageTemplateApproved(template.status))
                return;
              setTemplatePendingSend(template);
            }}
            allowSend={allowSend}
            allowDelete={allowDelete}
            isDefaultTemplateRow={
              isDefaultTemplatesScope ? isDefaultTemplateRow : undefined
            }
            organizationId={organizationId}
            currentPage={currentPage}
            onChangePage={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleTableRowsPerPageChange}
            allowEdit={allowEdit}
            hasActiveMetaEndpoint={resolvedHasActiveMetaEndpoint}
            onEditTemplate={handleEditTemplateGuarded}
            onDeleteTemplate={setTemplateIdPendingDelete}
          />
        ) : (
          <MessageTemplatesCardsView
            templates={paginatedTemplates}
            organizationId={organizationId}
            totalPages={totalPages}
            currentPage={currentPage}
            onChangePage={setCurrentPage}
            onSendTemplate={(template) => {
              if (!allowSend || !isMessageTemplateApproved(template.status))
                return;
              setTemplatePendingSend(template);
            }}
            allowSend={allowSend}
            allowDelete={allowDelete}
            showDefaultTemplateBadge={isDefaultTemplatesScope}
            allowEdit={allowEdit}
            hasActiveMetaEndpoint={resolvedHasActiveMetaEndpoint}
            onEditTemplate={handleEditTemplateGuarded}
            onDeleteTemplate={setTemplateIdPendingDelete}
          />
        )}

        {isCreateDialogOpen && showCreateButton && (
          <MessageTemplateFormDialog
            open={isCreateDialogOpen}
            onClose={() => {
              if (createFormFlow.isSubmitting) return;
              createFormFlow.resetForm();
              setIsCreateDialogOpen(false);
            }}
            organizationId={organizationId}
            control={createFormFlow.control}
            formErrors={createFormFlow.formErrors}
            handleSubmit={createFormFlow.handleSubmit}
            isSubmitting={createFormFlow.isSubmitting}
            hasSubmitError={createFormFlow.hasSubmitError}
            submitErrorMessage={createFormFlow.submitErrorMessage}
            resetForm={createFormFlow.resetForm}
            triggerBodyValidation={() =>
              createFormFlow.triggerFieldValidation("body")
            }
            triggerHeaderValidation={() =>
              createFormFlow.triggerFieldValidation("header")
            }
            setValue={createFormFlow.setValue}
            trigger={createFormFlow.trigger}
            questionnaires={questionnaireOptions}
            isQuestionnairesLoading={isQuestionnairesLoading}
            questionnairesErrorMessage={questionnairesErrorMessage}
            dialogTitle="Criar modelo de mensagem"
            submitLabel="Criar modelo"
            existingTemplateNames={templates.map((template) => template.name)}
          />
        )}

        {templateBeingEdited && (
          <MessageTemplateFormDialog
            open={Boolean(templateBeingEdited)}
            onClose={() => {
              if (editFormFlow.isSubmitting) return;
              setTemplateBeingEdited(null);
            }}
            organizationId={organizationId}
            control={editFormFlow.control}
            formErrors={editFormFlow.formErrors}
            handleSubmit={editFormFlow.handleSubmit}
            isSubmitting={editFormFlow.isSubmitting}
            hasSubmitError={editFormFlow.hasSubmitError}
            submitErrorMessage={editFormFlow.submitErrorMessage}
            resetForm={editFormFlow.resetForm}
            triggerBodyValidation={() =>
              editFormFlow.triggerFieldValidation("body")
            }
            triggerHeaderValidation={() =>
              editFormFlow.triggerFieldValidation("header")
            }
            setValue={editFormFlow.setValue}
            trigger={editFormFlow.trigger}
            questionnaires={questionnaireOptions}
            isQuestionnairesLoading={isQuestionnairesLoading}
            questionnairesErrorMessage={questionnairesErrorMessage}
            dialogTitle={
              editingDefaultTemplateUsageMessage
                ? "Editar modelo padrão"
                : "Editar modelo de mensagem"
            }
            submitLabel="Salvar alterações"
            existingTemplateNames={templates.map((template) => template.name)}
            currentTemplateName={templateBeingEdited.name}
            defaultTemplateUsageMessage={editingDefaultTemplateUsageMessage}
          />
        )}

        {templateIdPendingDelete && (
          <Box>
            <MessageTemplateDeleteConfirmDialog
              open={Boolean(templateIdPendingDelete)}
              onCancel={() => setTemplateIdPendingDelete(null)}
              onConfirm={async () => {
                if (!templateIdPendingDelete) return;
                await onDeleteTemplate({ templateId: templateIdPendingDelete });
                setTemplateIdPendingDelete(null);
              }}
            />
          </Box>
        )}

        {templatePendingSend && (
          <SendWhatsAppMessageDialog
            open={Boolean(templatePendingSend)}
            onClose={() => setTemplatePendingSend(null)}
            organizationId={organizationId}
            practitionerId={practitionerId}
            onMessageSent={onMessageSent}
            // No contexto de templates, não há appointment real ainda.
            // Mantemos o contrato "appointment via props" com um stub simples.
            appointment={{ id: "ad-hoc", startAtIso: dayjs().toISOString() }}
            template={{
              id: templatePendingSend.id,
              name: templatePendingSend.name,
              preview: templatePendingSend.preview,
              header: templatePendingSend.header,
              // Mantém o vínculo do questionário (botão URL) no envio pela aba de modelos.
              buttonValue: templatePendingSend.buttonValue,
            }}
            templates={messagingTemplatesForSend}
            questionnaires={questionnaireOptions}
            isQuestionnairesLoading={isQuestionnairesLoading}
            questionnairesErrorMessage={questionnairesErrorMessage}
            enforceMessageFranchiseGate
            franchiseRefreshSignal={franchiseRefreshSignal}
          />
        )}
      </Stack>
    );
  },
);

MessageTemplatesSection.displayName = "MessageTemplatesSection";

export default MessageTemplatesSection;
